import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { authService } from '../../../services/auth';
import { useFeedback } from '../../../utils/feedback';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { getOrderProductDisplayDimensions } from '../../orders/orderProductDisplay';
import { formatCurrency } from '../../../utils/currency';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import { roundWarehouseQuantity } from '../../../utils/warehouseQuantity';
import { formatUnit } from '../../../utils/units';
import { formatWarehouseQuantity } from '../warehouseQuantityConversion';
import { getWZErrorMessage, isBlockingWZError } from '../wzErrorMessages';
import { isFutureReleaseDay, releaseWZDocument } from '../wzReleaseFlow';
import {
  WZ_PURCHASE_INVOICE_WARNING_CODES,
  buildWZIssuePayload,
  createWZForIssue,
  fetchWZIssuePreview,
  type WZIssueLine,
  type WZIssuePreview,
} from '../wzIssueApi';
import {
  WZ_ISSUE_REASON_MIN_LENGTH,
  defaultQuantity,
  differsFromFifo,
  hasShortfall,
  isFifoQuantityValid,
  isManualLineValid,
  isPickValid,
  lineKey,
  manualParts,
  partsCost,
  partsWithoutPurchaseInvoice,
  picksFromFifo,
  sumParts,
  planFifo,
  type WZIssueMode,
  type WZIssuePart,
} from '../wzIssuePlan';
import './WzIssueModal.css';

const { Text } = Typography;

export interface WzIssueResult {
  id: string;
  wzNumber?: string;
  /** False when the WZ was saved but its release needs the operator (it stays a draft). */
  released: boolean;
}

export interface WzIssueModalProps {
  /** Customer order (ZK) the WZ is issued for. */
  orderId: string;
  open: boolean;
  onClose: () => void;
  /** Called after the WZ was created (and, normally, released). Refetch the order card here. */
  onIssued?: (result: WzIssueResult) => void;
}

type Picks = Record<string, Record<string, number | null>>;

/**
 * „Wystaw WZ” — issue goods of one customer order from the warehouse
 * (decision 2026-10-04, points 1–3; mockup „ZK w pełnym FIFO”).
 *
 * Automatically (FIFO): per line the quantity of this WZ (default: what is
 * left, capped by stock) and the batches the WZ takes, oldest first.
 * „Wybiorę partie”: the operator types what to take from each batch; a pick
 * that differs from FIFO needs a reason (min. 3 characters) which the backend
 * records in the WZ history (OperationalException WZ_BATCHES_NOT_FIFO), and
 * the WZ cost comes from the picked batches. A smaller quantity is a partial
 * delivery: the rest goes on a later WZ. The WZ carries no sale prices.
 *
 * Goods missing on stock (owner 2026-10-27): „W tej WZ” may exceed the
 * stock; the window then saves the WZ as a draft only (number + PDF, no stock
 * movement, no release confirmations) and the goods leave with „Rozlicz
 * wydanie” on the WZ once the PZ is received.
 *
 * „Adres dostawy” starts from the order address (else the customer seat) and
 * stays editable: the goods sometimes go elsewhere than the seat. The seat
 * and the order address are offered as quick choices; an empty field lets the
 * server use the same fallback.
 *
 * Reusable: mount it on the order card with `orderId`, `open`, `onClose`,
 * `onIssued`. It creates through `POST /wz` and releases through the shared
 * `wzReleaseFlow` (same confirmations as every other WZ entry point).
 */
const WzIssueModal: React.FC<WzIssueModalProps> = ({ orderId, open, onClose, onIssued }) => {
  const { t, i18n } = useTranslation(['wz', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const language = i18n.language || 'pl';
  const canSeeCost = authService.canViewWarehouseDocumentPurchasePrices();

  const [preview, setPreview] = useState<WZIssuePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [issueDate, setIssueDate] = useState<Dayjs>(dayjs());
  const [mode, setMode] = useState<WZIssueMode>('fifo');
  const [quantities, setQuantities] = useState<Record<string, number | null>>({});
  const [picks, setPicks] = useState<Picks>({});
  const [reason, setReason] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const loaded = await fetchWZIssuePreview(orderId);
      setPreview(loaded);
      setDeliveryAddress(loaded.deliveryAddress || '');
    } catch (error) {
      console.error('Error loading WZ issue preview:', error);
      setPreview(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!open) return;
    setIssueDate(dayjs());
    setMode('fifo');
    setQuantities({});
    setPicks({});
    setReason('');
    setDeliveryAddress('');
    void load();
  }, [open, load]);

  // Lines that can be issued at all (catalog product, something left to issue).
  const lines = useMemo(
    () => (preview?.lines || []).map((line, index) => ({ line, key: lineKey(line, index) })),
    [preview],
  );
  const issuable = lines.filter(({ line }) => line.productVariantId && line.remaining > 0);
  const unlinked = lines.filter(({ line }) => !line.productVariantId);

  const fifoQuantity = (line: WZIssueLine, key: string) => (
    quantities[key] === undefined ? defaultQuantity(line) : quantities[key]
  );
  const linePicks = (key: string) => picks[key] || {};

  // Lines of one product share the batches: FIFO walks them in order (planFifo).
  const fifoPlan = planFifo(issuable.map(({ line, key }) => ({
    line,
    key,
    quantity: mode === 'fifo' ? Number(fifoQuantity(line, key)) || 0 : sumParts(manualParts(line, linePicks(key))),
  })));
  const plan = issuable.map(({ line, key }) => {
    const take = fifoPlan.get(key) ?? { parts: [], shortfall: 0 };
    if (mode === 'fifo') {
      const quantity = fifoQuantity(line, key);
      const value = Number(quantity) || 0;
      return {
        line, key, quantity: value, parts: take.parts, shortfall: take.shortfall,
        valid: isFifoQuantityValid(line, quantity),
        notFifo: false,
      };
    }
    const parts = manualParts(line, linePicks(key));
    return {
      line, key, quantity: sumParts(parts), parts, shortfall: 0,
      valid: isManualLineValid(line, linePicks(key)),
      notFifo: differsFromFifo(line, linePicks(key), take.parts),
    };
  });
  const notFifo = mode === 'manual' && plan.some((entry) => entry.notFifo);
  // WP12: goods without an approved purchase invoice — shown here with the
  // reason field instead of a second dialog after „Wystaw WZ” (the release
  // still requires the reason; it gets the one typed here).
  // Goods missing on stock: save as a draft only; the release (and its invoice
  // reason) comes later with „Rozlicz wydanie” on the WZ.
  const draftOnly = mode === 'fifo' && hasShortfall(plan);
  const withoutInvoice = draftOnly ? [] : partsWithoutPurchaseInvoice(plan);
  const needsReason = notFifo || withoutInvoice.length > 0;
  const reasonOk = !needsReason || reason.trim().length >= WZ_ISSUE_REASON_MIN_LENGTH;
  const partial = plan.some((entry) => entry.quantity > 0 && entry.quantity < entry.line.remaining - 0.0005);
  const quantitiesOk = plan.every((entry) => entry.valid) && plan.some((entry) => entry.quantity > 0);
  const canIssue = Boolean(preview) && quantitiesOk && reasonOk && !submitting && issueDate?.isValid();

  const qty = (value: number, unit: string) => formatWarehouseQuantity(value, unit, language);
  const unitCost = (value: number | null | undefined, unit: string) => (
    value === null || value === undefined
      ? '—'
      : t('wz:issueModal.unitCost', { cost: formatCurrency(value, 'PLN'), unit: formatUnit(unit, language) })
  );

  const switchMode = (next: WZIssueMode) => {
    if (next === 'manual' && mode !== 'manual') {
      // Start from what FIFO would take, so only the change has to be typed.
      const seeded: Picks = {};
      const used = new Map<string, number>();
      issuable.forEach(({ line, key }) => {
        seeded[key] = picksFromFifo(line, Number(fifoQuantity(line, key)) || 0, used);
      });
      setPicks(seeded);
    }
    setMode(next);
  };

  // One row per batch: which PZ, when it arrived, how much this WZ takes (and at what cost) — readable when there are many.
  const describeParts = (parts: WZIssuePart[], line: WZIssueLine) => (
    parts.length === 0 ? '—' : (
      <div className="wz-batches" data-testid="wz-issue-batches">
        {parts.map((part) => (
          <div className="wz-batches__row" key={part.batch.inventoryBatchId}>
            <b>{part.batch.pzNumber || t('wz:issueModal.unnamedBatch')}</b>
            <span>{t('wz:issueModal.batchReceived', { date: formatDateEuropean(part.batch.receiptDate) })}</span>
            <span className="wz-batches__qty">{qty(part.quantity, line.unit)}</span>
            {canSeeCost && part.batch.unitCostPLN !== undefined && <span>{unitCost(part.batch.unitCostPLN, line.unit)}</span>}
          </div>
        ))}
      </div>
    )
  );

  // The product always comes with its dimensions, small, under the name.
  const productCell = (line: WZIssueLine) => {
    const dimensions = getOrderProductDisplayDimensions(line);
    return (
      <div>
        <Text strong>{line.productName}</Text>
        {dimensions && <div><Text type="secondary" style={{ fontSize: 12 }} data-testid="wz-issue-dimensions">{dimensions}</Text></div>}
      </div>
    );
  };

  const handleIssue = async () => {
    if (!preview || !canIssue) return;
    setSubmitting(true);
    try {
      const payload = buildWZIssuePayload({
        orderId,
        issueDate: issueDate.format('YYYY-MM-DD'),
        deliveryAddress: isPickup ? null : deliveryAddress,
        lines: plan.map((entry) => ({
          line: entry.line,
          quantity: entry.quantity,
          ...(mode === 'manual'
            ? { picks: entry.parts.map((part) => ({ inventoryBatchId: part.batch.inventoryBatchId, quantity: part.quantity })) }
            : {}),
        })),
      });
      const created = await createWZForIssue(payload, {
        modal, t: t as TFunction, reason: notFifo ? reason.trim() : undefined,
      });
      if (!created?.id) return;
      if (draftOnly) {
        feedback.success(t('wz:issueModal.savedAsDraftShortfall', { wzNumber: created.wzNumber || '' }), { sticky: true });
        onIssued?.({ id: created.id, wzNumber: created.wzNumber, released: false });
        onClose();
        return;
      }
      const released = await releaseWZDocument({
        wzId: created.id,
        wzNumber: created.wzNumber,
        modal,
        t,
        language,
        feedback,
        // „Data wydania” of this window is the release date (2026-10-14).
        releaseDate: issueDate.format('YYYY-MM-DD'),
        ...(withoutInvoice.length > 0
          ? { presetReason: { codes: WZ_PURCHASE_INVOICE_WARNING_CODES, reason: reason.trim() } }
          : {}),
      });
      if (!released) {
        feedback.warning(t('wz:issueModal.savedAsDraft', { wzNumber: created.wzNumber || '' }), { sticky: true });
      }
      onIssued?.({ id: created.id, wzNumber: created.wzNumber, released });
      onClose();
    } catch (error) {
      console.error('Error issuing WZ:', error);
      feedback.error(getWZErrorMessage(error, t, 'wz:errors.createFailed'), { sticky: isBlockingWZError(error) });
    } finally {
      setSubmitting(false);
    }
  };

  const isPickup = preview?.deliveryMode === 'CUSTOMER_PICKUP';
  // Quick choices: the order „Adres dostawy” and the customer seat. The
  // preview address already falls back to the seat, so when both are equal the
  // order has no address of its own: only „Siedziba klienta” is offered.
  const seatAddress = preview?.recipientAddress?.trim() || '';
  const orderAddress = preview?.deliveryAddress?.trim() || '';
  const addressChoices = [
    ...(orderAddress && orderAddress !== seatAddress
      ? [{ address: orderAddress, labelKey: 'wz:issueModal.addressFromOrder' }]
      : []),
    ...(seatAddress ? [{ address: seatAddress, labelKey: 'wz:issueModal.addressSeat' }] : []),
  ];

  const fifoTable = (
    <Table
      size="small"
      pagination={false}
      rowKey="key"
      data-testid="wz-issue-fifo-table"
      dataSource={plan}
      columns={[
        {
          title: t('wz:issueModal.product'),
          key: 'product',
          render: (_value, entry) => productCell(entry.line),
        },
        {
          title: t('wz:issueModal.toIssue'),
          key: 'remaining',
          align: 'right',
          render: (_value, entry) => qty(entry.line.remaining, entry.line.unit),
        },
        {
          title: t('wz:issueModal.inThisWz'),
          key: 'quantity',
          align: 'right',
          render: (_value, entry) => (
            <InputNumber
              aria-label={t('wz:issueModal.quantityAria', { product: entry.line.productName })}
              min={0}
              step={1}
              // Polish amount as elsewhere („10”, „2,5”), not „10.000”; 3 decimals at most.
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
              status={entry.valid ? undefined : 'error'}
              value={fifoQuantity(entry.line, entry.key)}
              onChange={(value) => setQuantities((current) => ({
                ...current, [entry.key]: value === null ? null : roundWarehouseQuantity(value),
              }))}
              addonAfter={formatUnit(entry.line.unit, language)}
              style={{ width: 160 }}
            />
          ),
        },
        {
          title: t('wz:issueModal.batchesTaken'),
          key: 'batches',
          render: (_value, entry) => {
            const { shortfall } = entry;
            return (
              <Space direction="vertical" size={0}>
                {describeParts(entry.parts, entry.line)}
                {shortfall > 0 && (
                  <Text type="danger">{t('wz:issueModal.shortfall', { quantity: qty(shortfall, entry.line.unit) })}</Text>
                )}
              </Space>
            );
          },
        },
      ]}
    />
  );

  const manualRows = plan.flatMap((entry) => [
    { kind: 'group' as const, key: `group-${entry.key}`, entry },
    ...entry.line.batches.map((batch, index) => ({ kind: 'batch' as const, key: `${entry.key}-${batch.inventoryBatchId}`, entry, batch, index })),
  ]);
  const manualColumnCount = canSeeCost ? 5 : 4;
  const manualTable = (
    <Table
      size="small"
      pagination={false}
      rowKey="key"
      data-testid="wz-issue-pick-table"
      dataSource={manualRows}
      columns={[
        {
          title: t('wz:issueModal.batch'),
          key: 'batch',
          onCell: (row) => (row.kind === 'group' ? { colSpan: manualColumnCount } : {}),
          render: (_value, row) => {
            if (row.kind === 'group') {
              const status = row.entry.quantity > row.entry.line.remaining ? 'danger'
                : (row.entry.quantity === row.entry.line.remaining ? 'success' : 'secondary');
              return (
                <Space wrap>
                  {productCell(row.entry.line)}
                  <Text type={status}>
                    {t('wz:issueModal.pickedOf', {
                      picked: qty(row.entry.quantity, row.entry.line.unit),
                      remaining: qty(row.entry.line.remaining, row.entry.line.unit),
                    })}
                  </Text>
                </Space>
              );
            }
            return (
              <Space>
                <Text strong>{row.batch.pzNumber || t('wz:issueModal.unnamedBatch')}</Text>
                {row.index === 0 && <Tag color="blue">{t('wz:issueModal.oldest')}</Tag>}
              </Space>
            );
          },
        },
        {
          title: t('wz:issueModal.received'),
          key: 'received',
          onCell: (row) => (row.kind === 'group' ? { colSpan: 0 } : {}),
          render: (_value, row) => (row.kind === 'batch' ? formatDateEuropean(row.batch.receiptDate) : null),
        },
        {
          title: t('wz:issueModal.available'),
          key: 'available',
          align: 'right',
          onCell: (row) => (row.kind === 'group' ? { colSpan: 0 } : {}),
          render: (_value, row) => (row.kind === 'batch' ? qty(row.batch.available, row.entry.line.unit) : null),
        },
        ...(canSeeCost ? [{
          title: t('wz:issueModal.cost'),
          key: 'cost',
          align: 'right' as const,
          onCell: (row: typeof manualRows[number]) => (row.kind === 'group' ? { colSpan: 0 } : {}),
          render: (_value: unknown, row: typeof manualRows[number]) => (
            row.kind === 'batch' ? unitCost(row.batch.unitCostPLN, row.entry.line.unit) : null
          ),
        }] : []),
        {
          title: t('wz:issueModal.take'),
          key: 'take',
          align: 'right',
          onCell: (row) => (row.kind === 'group' ? { colSpan: 0 } : {}),
          render: (_value, row) => {
            if (row.kind !== 'batch') return null;
            const value = linePicks(row.entry.key)[row.batch.inventoryBatchId];
            return (
              <InputNumber
                aria-label={t('wz:issueModal.pickAria', { pz: row.batch.pzNumber || '', product: row.entry.line.productName })}
                min={0}
                step={1}
                {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                status={isPickValid(row.batch, value) ? undefined : 'error'}
                value={value ?? null}
                onChange={(next) => setPicks((current) => ({
                  ...current,
                  [row.entry.key]: { ...(current[row.entry.key] || {}), [row.batch.inventoryBatchId]: next === null ? null : roundWarehouseQuantity(next) },
                }))}
                addonAfter={formatUnit(row.entry.line.unit, language)}
                style={{ width: 160 }}
              />
            );
          },
        },
      ]}
    />
  );

  // Priced like the release (shared-unit share of the batch), so the window
  // and the WZ / ZK card show the same grosz.
  const entryCosts = plan.map((entry) => partsCost(entry.parts, () => entry.line));
  const totalCost = canSeeCost && entryCosts.every((cost) => cost !== null)
    ? Math.round(entryCosts.reduce<number>((sum, cost) => sum + (cost as number), 0) * 100) / 100
    : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={980}
      destroyOnHidden
      title={(
        <Space direction="vertical" size={0}>
          <span>{t('wz:issueModal.title')}</span>
          <Text type="secondary" style={{ fontSize: 13 }}>{t('wz:issueModal.subtitle')}</Text>
        </Space>
      )}
      footer={(
        <Space wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
          {!reasonOk && <Text type="warning">{t(notFifo ? 'wz:issueModal.reasonRequired' : 'wz:issueModal.reasonRequiredInvoice')}</Text>}
          {reasonOk && !quantitiesOk && preview && <Text type="warning">{t('wz:issueModal.checkQuantities')}</Text>}
          <Button onClick={onClose}>{t('common:cancel')}</Button>
          <Button type="primary" onClick={() => { void handleIssue(); }} disabled={!canIssue} loading={submitting}>
            {t(draftOnly ? 'wz:issueModal.saveDraft' : 'wz:issueModal.issue')}
          </Button>
        </Space>
      )}
    >
      <Spin spinning={loading}>
        {loadFailed && (
          <Alert
            type="error"
            showIcon
            message={t('wz:issueModal.loadFailed')}
            action={<Button size="small" onClick={() => { void load(); }}>{t('wz:issueModal.retry')}</Button>}
          />
        )}
        {preview && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space wrap size={16} style={{ width: '100%' }}>
              <div>
                <Text strong>{t('wz:fields.releaseDate')}</Text>
                <div>
                  <DatePicker
                    aria-label={t('wz:fields.releaseDate')}
                    value={issueDate}
                    onChange={(value) => setIssueDate(value || dayjs())}
                    format="DD.MM.YYYY"
                    allowClear={false}
                    disabledDate={isFutureReleaseDay}
                  />
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                  {t('wz:confirm.releaseDateHelp')}
                </Text>
              </div>
              <div style={{ minWidth: 280, flex: 1 }}>
                {isPickup ? (
                  <>
                    <Text strong>{t('wz:issueModal.pickup')}</Text>
                    <Input value={t('wz:issueModal.pickupValue')} disabled aria-label={t('wz:issueModal.pickup')} />
                  </>
                ) : (
                  <>
                    <Text strong>{t('wz:fields.deliveryAddress')}</Text>
                    <Input
                      data-testid="wz-issue-delivery-address"
                      aria-label={t('wz:fields.deliveryAddress')}
                      value={deliveryAddress}
                      onChange={(event) => setDeliveryAddress(event.target.value)}
                      placeholder={t('wz:placeholders.deliveryAddress')}
                      maxLength={500}
                    />
                    <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                      {t('wz:issueModal.deliveryAddressHelp')}
                    </Text>
                    {addressChoices.length > 0 && (
                      <Space size={4} wrap style={{ marginTop: 4 }}>
                        {addressChoices.map((choice) => (
                          <Button
                            key={choice.labelKey}
                            size="small"
                            type={deliveryAddress.trim() === choice.address.trim() ? 'primary' : 'default'}
                            ghost={deliveryAddress.trim() === choice.address.trim()}
                            title={choice.address}
                            onClick={() => setDeliveryAddress(choice.address)}
                          >
                            {t(choice.labelKey)}
                          </Button>
                        ))}
                      </Space>
                    )}
                  </>
                )}
              </div>
            </Space>

            {issuable.length === 0 ? (
              <Alert type="info" showIcon message={t('wz:issueModal.nothingToIssue')} />
            ) : (
              <>
                <Segmented
                  value={mode}
                  onChange={(value) => switchMode(value as WZIssueMode)}
                  options={[
                    { value: 'fifo', label: t('wz:issueModal.modeFifo') },
                    { value: 'manual', label: t('wz:issueModal.modeManual') },
                  ]}
                />
                {mode === 'fifo' ? fifoTable : manualTable}
                {draftOnly && (
                  <Alert
                    type="info"
                    showIcon
                    data-testid="wz-issue-draft-only"
                    message={t('wz:issueModal.draftOnlyTitle')}
                    description={t('wz:issueModal.draftOnlyDescription')}
                  />
                )}
                {withoutInvoice.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    data-testid="wz-issue-without-invoice"
                    message={t('wz:issueModal.invoiceWarning', { products: withoutInvoice.join(', ') })}
                  />
                )}
                {(mode === 'manual' || withoutInvoice.length > 0) && (
                  <div>
                    <Text strong>
                      {t(withoutInvoice.length > 0 && mode !== 'manual' ? 'wz:issueModal.invoiceReasonLabel' : 'wz:issueModal.reasonLabel')}
                      {needsReason ? ' *' : ''}
                    </Text>
                    <Input
                      data-testid="wz-issue-reason"
                      aria-label={t(withoutInvoice.length > 0 && mode !== 'manual' ? 'wz:issueModal.invoiceReasonLabel' : 'wz:issueModal.reasonLabel')}
                      value={reason}
                      maxLength={500}
                      status={reasonOk ? undefined : 'error'}
                      placeholder={t('wz:issueModal.reasonPlaceholder')}
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </div>
                )}
                {notFifo && (
                  <Alert type="warning" showIcon data-testid="wz-issue-not-fifo" message={t('wz:issueModal.notFifoWarning')} />
                )}
              </>
            )}

            {unlinked.length > 0 && (
              <Alert
                type="warning"
                showIcon
                data-testid="wz-issue-unlinked-lines"
                message={t('wz:issueModal.unlinkedLines', {
                  // Each product with its dimensions in brackets, as in the table above.
                  products: unlinked.map(({ line }) => {
                    const dimensions = getOrderProductDisplayDimensions(line);
                    return dimensions ? `${line.productName} (${dimensions})` : line.productName;
                  }).join(', '),
                })}
              />
            )}
            {totalCost !== null && plan.some((entry) => entry.quantity > 0) && (
              <Text type="secondary">{t('wz:issueModal.totalCost', { cost: formatCurrency(totalCost, 'PLN') })}</Text>
            )}
            {(mode === 'manual' || partial) && (
              <Alert
                type="info"
                showIcon
                data-testid="wz-issue-hint"
                message={[
                  mode === 'manual' ? t('wz:issueModal.hintManual') : null,
                  partial ? t('wz:issueModal.hintPartial') : null,
                ].filter(Boolean).join(' ')}
              />
            )}
          </Space>
        )}
      </Spin>
    </Modal>
  );
};

export default WzIssueModal;
